import {
	type FetchFanShopProductVariantsData,
	minutesToMs,
	type PaginationWithSimplePaginationMode
} from '@capsicum/common';
import { type InfiniteData, useInfiniteQuery, type UseInfiniteQueryOptions } from '@tanstack/react-query';

import { validationRule } from '../../util';
import { fanShopApi } from '../api';

export const UseProductsQueryKeyBuilder = (
	partnerIds: Array<string> = [],
	productCategoriesIds: Array<string> = [],
	limit: number,
	isTestValidId?: boolean
) =>
	[
		'fanshopProductUpdateFromWS',
		'fanShop',
		'products',
		...partnerIds,
		...productCategoriesIds,
		limit,
		isTestValidId
	] as const;

/**
 * Returns all products
 */

export const useProductsQueries = (
	partnerIds?: Array<string>,
	productCategoriesIds?: Array<string>,
	limit = 10,
	options?: Omit<
		UseInfiniteQueryOptions<FetchFanShopProductVariantsData, Error, InfiniteData<FetchFanShopProductVariantsData>>,
		'queryFn' | 'queryKey'
	>
) => {
	const isTestValidId = partnerIds && partnerIds?.every(validationRule);

	return useInfiniteQuery({
		...options,
		enabled: !!productCategoriesIds?.length,
		getNextPageParam: lastPage => {
			const metadata = lastPage.metadata as PaginationWithSimplePaginationMode['metadata'];

			const { current_page = 1, has_more_page } = metadata ?? {};

			return has_more_page ? current_page + 1 : undefined;
		},
		initialPageParam: 1,
		queryFn: async ({ pageParam }) => {
			return (await fanShopApi).fetchFanShopProductVariants({
				'details[]': [
					'productTranslations',
					'productCategoryTranslations',
					'productVariantTranslations',
					'productImages',
					'productTierLevelTranslations',
					'tagTranslations',
					'productTypeTranslations',
					'countryTranslations',
					'productVariantImages'
				],
				direction: 'asc',
				limit,
				page: pageParam as number | undefined,
				paginationMode: 'simple',
				'productCategoriesIds[]': productCategoriesIds,
				'productRedeemTypes[]': ['regular', 'reverseAuction'],
				...(isTestValidId && { 'partnerIds[]': partnerIds })
			});
		},
		queryKey: UseProductsQueryKeyBuilder(partnerIds, productCategoriesIds, limit, isTestValidId),
		staleTime: minutesToMs(5)
	});
};
